import { useFormik } from "formik";
import { Button } from "primereact/button";
import { useFragment, useMutation } from "react-relay";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type fileElementForm_EditFileElementMutation } from "@relay/fileElementForm_EditFileElementMutation.graphql";
import { EDIT_FILE_ELEMENT_MUTATION, FILE_ELEMENTV2_FRAGMENT } from "./file-element-form.graphql";

import { type FileElementFormProps, type FileElementFormState } from "./file-element-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField, type RenderConfig } from "../../../../components/ValidatedField";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import {
	resetArrayOfEditedForms,
	addEditedFormToEditedFormsArray,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { stripHtml } from "../../../../util/html.utils";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const FileElementForm = ({
	fileElementV2FragmentRef,
	onBack,
	fileTypeOptions,
}: FileElementFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const fileElement = useFragment(FILE_ELEMENTV2_FRAGMENT, fileElementV2FragmentRef);

	const formId = "AcademiesFileElementForm";
	const [editFileElement, isEditingFileElement] =
		useMutation<fileElementForm_EditFileElementMutation>(EDIT_FILE_ELEMENT_MUTATION);
	const dispatch = useTypedDispatch();
	const formik = useFormik<FileElementFormState>({
		initialValues: {
			title: stripHtml(fileElement.title),
			file: { ...fileElement.file } as any as FileV2,
		},
		validationSchema: Yup.object().shape({
			title: Yup.string().required(t("validation:titleFieldRequired")),
			file: Yup.object().required(t("validation:fileFieldRequired")),
		}),
		onSubmit: (values) => {
			editFileElement({
				variables: {
					input: {
						fileElementId: fileElement.id,
						title: values.title,
						fileId: values.file?.id,
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
				updater: setDraft,
			});
		},
	});

	const canUpload = useHasPermissions(["UserInAccountPermission_Nodes_UpdateNodes"]);
	const canDelete = useHasPermissions("onlyOwnerOfRoot");

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<FileElementFormState, string>
				name={"title"}
				label={t("general.title")}
				component={DefaultTextFieldComponent}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				formikConfig={formik}
			/>
			<ValidatedField<FileElementFormState, FileV2>
				name={"file"}
				label={t("forms.file")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({
					fieldName,
					fieldValue,
					updateField,
					onChange,
				}: RenderConfig<FileV2>) => {
					return (
						<FileSelectionField
							name={fieldName}
							selectedFile={fieldValue}
							setSelectedFile={updateField}
							onChange={onChange}
							fileTypeOptions={fileTypeOptions}
							canUploadFiles={canUpload}
							canDeleteFiles={canDelete}
						/>
					);
				}}
				formikConfig={formik}
			/>

			<Button
				disabled={isEditingFileElement}
				type="submit"
				label={t("actions.save")}
				className="mt-2"
			/>

			<Button
				type="button"
				onClick={() => {
					onBack();
				}}
				label={t("actions.back")}
				className="p-button-secondary mt-2"
			/>
		</form>
	);
};
